import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';

const TermsAcceptanceContext = createContext();

export const useTermsAcceptance = () => {
  const context = useContext(TermsAcceptanceContext);
  if (!context) throw new Error('useTermsAcceptance must be used within TermsAcceptanceProvider');
  return context;
};

export const TermsAcceptanceProvider = ({ children }) => {
  const { user, clinic } = useAuth();
  const [latestTerms, setLatestTerms] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    if (!user || !clinic) {
      setShowModal(false);
      return;
    }
    checkTermsAcceptance();
  }, [user?.id, clinic?.id]);

  const checkTermsAcceptance = async () => {
    try {
      // Only show to the clinic owner or vets with role 'admin'
      const isOwner = clinic.user_id === user.id;
      if (!isOwner) {
        const { data: vet } = await supabase
          .from('vets')
          .select('role')
          .eq('user_id', user.id)
          .eq('clinic_id', clinic.id)
          .single();
        if (!vet || vet.role !== 'admin') {
          setShowModal(false);
          return;
        }
      }

      const { data: terms, error: termsError } = await supabase
        .from('terms')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (termsError && termsError.code === 'PGRST116') {
        // No terms exist yet — nothing to enforce
        setShowModal(false);
        return;
      }
      if (termsError) {
        if (import.meta.env.DEV) console.error('Error fetching terms:', termsError);
        return;
      }

      setLatestTerms(terms);

      const { data: acceptance, error: acceptanceError } = await supabase
        .from('clinic_terms_acceptance')
        .select('id')
        .eq('clinic_id', clinic.id)
        .eq('terms_id', terms.id)
        .single();

      if (acceptanceError && acceptanceError.code === 'PGRST116') {
        // Not yet accepted — show modal
        setShowModal(true);
      } else if (acceptanceError) {
        if (import.meta.env.DEV) console.error('Error checking acceptance:', acceptanceError);
      } else if (acceptance) {
        setShowModal(false);
      }
    } catch (err) {
      if (import.meta.env.DEV) console.error('Error in checkTermsAcceptance:', err);
    }
  };

  const acceptTerms = async () => {
    if (!latestTerms || !clinic || !user) return;
    try {
      const { error } = await supabase
        .from('clinic_terms_acceptance')
        .insert({
          clinic_id: clinic.id,
          terms_id: latestTerms.id,
          accepted_by: user.id,
        });
      if (error) throw error;
      setShowModal(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error('Error accepting terms:', err);
    }
  };

  return (
    <TermsAcceptanceContext.Provider value={{ latestTerms, showModal, acceptTerms }}>
      {children}
    </TermsAcceptanceContext.Provider>
  );
};
